import { MovParcelaResponseModel } from "api/cardapio/models/mov-pag/mov-registrar-response"
import React, { useCallback, useMemo } from "react"
import QRCode from 'qrcode.react';
import { Box, Button, Card, Fade, Grid, Typography, useTheme } from "@material-ui/core";
import { TextFieldSaurus } from "components/inputs/text-field-saurus";
import { useNotificacoes } from "components";
import { CopiarIcon } from "components/icons/copiar-icon";
import { useStyles } from "./pagamento-pix-detalhes-styles";
import { CardapioStorageKeys, useCardapioStorage } from "storage/cardapio-storage";
import { CardapioDadosProps } from "modulos/cardapio/containers/cardapio/cardapio-header/cardapio-header-props";
import { EnumStatusPix } from "../../pagamento-pix";
import { toDecimalString } from "utils/to-decimal";
import { ErroIcon } from "components/icons/erro-icon";
import { MovSimplesProdutoModel } from "api/cardapio/models/produto/mov-simples-produto-model";
import { usePedidos } from "services/providers/pedidos-provider";

export interface PagamentoPixProps {
    model: MovParcelaResponseModel;
    carregandoCriarPedido: boolean;
    statusPix: EnumStatusPix;
    handlePedido: () => Promise<void>;
    cancelarPedido: () => void;
    fechar: () => void;
    pedidoErro: boolean;
    handlePedidoPix: () => Promise<void>;
}

export const PagamentoPixDetalhes = ({ model, carregandoCriarPedido, statusPix, handlePedido, cancelarPedido, pedidoErro, handlePedidoPix }: PagamentoPixProps) => {
    const { showSuccessMessage, showErrorMessage } = useNotificacoes();
    const { getRegistro } = useCardapioStorage()
    const dadosGerais = getRegistro(CardapioStorageKeys.DadosGerais, false) as CardapioDadosProps;
    const sacola = getRegistro(CardapioStorageKeys.Sacola, false) as MovSimplesProdutoModel[] | null
    const { empresa } = dadosGerais
    const { isPedidoDelivery } = usePedidos()
    const classes = useStyles()
    const theme = useTheme()

    const copiarCodigo = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(model.pix?.pixCopiaCola ?? '')
            showSuccessMessage('Código copiado!')
        } catch (err: any) {
            showErrorMessage('Ocorreu um erro ao tentar copiar o código, tente novamente.')
        }
    }, [model.pix?.pixCopiaCola, showErrorMessage, showSuccessMessage])

    const btnCopiar = useMemo(() => (
        <Box mt={"2px"}>
            <Button className={classes.btnIcon} variant="text" onClick={copiarCodigo}>
                <CopiarIcon tipo={theme.palette.type === "light" ? 'BUTTON' : 'BUTTON_PRIMARY'} />
            </Button>
        </Box>
    ), [classes.btnIcon, copiarCodigo, theme.palette.type])

    const tempoExpiracao = useMemo(() => {
        const data = new Date(model.dataRegistro)

        const expiracao = new Date(data.setMinutes(data.getMinutes() + 30))
        const horas = ('00' + expiracao.getHours()).substr(-2)
        const minutos = ('00' + expiracao.getMinutes()).substr(-2)
        return `${horas}:${minutos}`
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const preco = (sacola ?? [])?.reduce((a, b) => a + b.vFinal, 0) ?? 0


    return (
        <>
            {pedidoErro && (
                <Fade in>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box textAlign={"center"} mb={3}>
                                <Box mb={3}>
                                    <ErroIcon tipo={theme.palette.type === 'dark' ? "BUTTON_PRIMARY" : 'BUTTON'} style={{ width: 60, height: 60 }} />
                                </Box>
                                <Typography>
                                    Ocorreu um problema ao consultar o pagamento, {!isPedidoDelivery ? "pague na entrega" : ''} ou entre em contato conosco pelo telefone {empresa.fone}.
                                </Typography>
                            </Box>
                        </Grid>
                        {!isPedidoDelivery && (
                            <Grid item xs={12} container justify="center">
                                <Button variant="contained" fullWidth size="large" onClick={handlePedidoPix}>
                                    Pagar na entrega
                                </Button>
                            </Grid>
                        )}
                        <Grid item xs={12} container justify="center">
                            <Button
                                variant="outlined"
                                fullWidth
                                color={theme.palette.type === "light" ? "primary" : "inherit"}
                                onClick={cancelarPedido}
                            >
                                Cancelar Pedido
                            </Button>
                        </Grid>
                    </Grid>
                </Fade >
            )}
            {statusPix === EnumStatusPix.Neutro && !carregandoCriarPedido && !pedidoErro ? (
                <Fade in>
                    <Grid container spacing={2}>
                        <Grid item md={12} xs={12}>
                            <Box textAlign={"center"} mb={3} color="textPrimary">
                                <Typography variant="h5" style={{ fontWeight: 700, fontSize: 22 }}>
                                    Aguardando recebimento do PIX
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="center" mb={3}>
                                <Card style={{ boxShadow: 'none', backgroundColor: theme.palette.type === 'light' ? '#f2f2f2' : "rgb(85 85 85)" }}>
                                    <Box p={1} minWidth={300} textAlign="center" display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography color="textPrimary" variant="body1" style={{ fontWeight: 600 }}>
                                            Valor à pagar
                                        </Typography>
                                        <Typography color={theme.palette.type === "light" ? "primary" : "textPrimary"} variant="h5" style={{ fontWeight: 600 }}>
                                            R$ {toDecimalString(preco ?? 0)}
                                        </Typography>
                                    </Box>
                                </Card>
                            </Box>
                            <Box width="100%" justifyContent={"center"} display={"flex"} mb={3} className={classes.boxQRCode}>
                                <QRCode
                                    bgColor={"#FFFFFF"}
                                    value={model.pix?.pixCopiaCola ?? ''}
                                    size={250}
                                    renderAs="svg"
                                />
                            </Box>
                            <Box textAlign={"center"} mb={1}>
                                <Typography>
                                    Escaneie o código QR ou copie o código abaixo para pagar via PIX:
                                </Typography>
                            </Box>
                            <TextFieldSaurus
                                color="primary"
                                value={model.pix?.pixCopiaCola ?? ''}
                                className={classes.inputPix}
                                endAdornmentButton={btnCopiar}
                            />
                            <Box mt={1} textAlign="center">
                                <Typography>
                                    O tempo para pagar irá acabar às: <span style={{ fontWeight: 600, fontSize: 20 }}>{tempoExpiracao}</span>
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid >
                </Fade >
            ) : !carregandoCriarPedido && statusPix === EnumStatusPix.Cancelado && !pedidoErro && (
                <Fade in>
                    <Box height="100%" alignItems="center" display="flex">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box textAlign={"center"} mb={3}>
                                    <Box mb={3}>
                                        <ErroIcon tipo={theme.palette.type === 'dark' ? "BUTTON_PRIMARY" : 'BUTTON'} style={{ width: 60, height: 60 }} />
                                    </Box>
                                    <Typography>
                                        PIX cancelado, ultrapassou o tempo limite de pagamento. Por conta disso, não temos como concluir seu pagamento.
                                    </Typography>
                                    <Box mt={2}>
                                        <Typography>
                                            Caso necessário, entre em contato conosco pelo telefone {empresa.fone}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            {!isPedidoDelivery && (
                                <Grid item md={6} xs={12}>
                                    <Button variant="contained" fullWidth onClick={handlePedido}>
                                        Pagar depois
                                    </Button>
                                </Grid>
                            )}
                            <Grid item md={isPedidoDelivery ? 12 : 6}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    color={theme.palette.type === "light" ? "primary" : "inherit"}
                                    onClick={cancelarPedido}
                                >
                                    Cancelar Pedido
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Fade>
            )
            }
        </>
    )
}